import { supabase } from "@/integrations/supabase/client";
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  safeDispatchEvent,
  safeLocalStorageGet,
  safeLocalStorageRemove,
  safeLocalStorageSet,
  safeSessionStorageGet,
  safeSessionStorageRemove,
  safeSessionStorageSet,
} from "@/lib/safe-browser";
import type { SavedRouteVenue } from "./saved-route-venues";

export type SavedStop = {
  name: string;
  category: string;
  blurb: string;
  priceLocal: number;
  rating: number;
  minutes?: number;
  day?: number;
  lat?: number;
  lng?: number;
  placeId?: string;
  photoUrl?: string;
  /** Real venues resolved when the route was created, anchored after this stop. */
  venuesAfter?: SavedRouteVenue[];
};


export type SavedItinerary = {
  id: string;
  user_id: string;
  title: string;
  city: string;
  country: string | null;
  lang: string | null;
  mode: string;
  start_time: string | null;
  duration_hours: number | null;
  stops: SavedStop[];
  stops_count?: number | null;
  notes: string | null;
  share_slug: string | null;
  /** Rotas privadas nunca são partilháveis, mesmo com link antigo. */
  private: boolean;
  /** true = promovida a favorita com "Guardar"; false = guardada automaticamente em "Recentes". */
  favorite?: boolean;
  created_at: string;
  updated_at: string;
};

const PENDING_KEY = "routify_load_itinerary";

export type SaveItineraryInput = {
  title: string;
  city: string;
  country?: string | null;
  lang?: string | null;
  mode: string;
  start_time?: string | null;
  duration_hours?: number | null;
  stops: SavedStop[];
  notes?: string | null;
  origin?: "free" | "premium" | "pro" | "concierge";
  /** Marca a rota como privada — nunca pode gerar link de partilha. */
  private?: boolean;
};

function clearSavedRoutesCache(userId?: string) {
  if (!userId || typeof window === "undefined") return;
  try {
    const key = `routify:saved-routes:${userId}`;
    safeSessionStorageRemove(key);
    safeLocalStorageRemove(key);
  } catch {
    // cache opcional
  }
}

export async function saveItinerary(input: SaveItineraryInput) {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("not_authenticated");
  // Pressing "Guardar" promotes the route to a permanent favourite.
  const { data, error } = await supabase
    .from("saved_itineraries")
    .insert({ ...input, user_id: auth.user.id, favorite: true })
    .select()
    .single();
  if (error) throw error;
  clearSavedRoutesCache(auth.user.id);
  return data as unknown as SavedItinerary;
}

/**
 * Regista automaticamente uma rota concluída em "Recentes" para utilizadores
 * autenticados, sem exigir que carreguem em "Guardar". Estas entradas ficam
 * com `favorite: false` — só passam a permanentes quando o utilizador prime
 * "Guardar" (ver `saveItinerary` / `promoteToFavorite`).
 */
export async function recordRecentItinerary(input: SaveItineraryInput) {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("not_authenticated");
  const { data, error } = await supabase
    .from("saved_itineraries")
    .insert({ ...input, user_id: auth.user.id, favorite: false })
    .select()
    .single();
  if (error) throw error;
  clearSavedRoutesCache(auth.user.id);
  return data as unknown as SavedItinerary;
}

/** Promove uma rota de "Recentes" (automática) a favorita/permanente. */
export async function promoteToFavorite(id: string) {
  const { error } = await supabase
    .from("saved_itineraries")
    .update({ favorite: true })
    .eq("id", id);
  if (error) throw error;
}

const GUEST_RECENT_KEY = "routify_guest_recent_itineraries";
const GUEST_RECENT_LIMIT = 5;

/** Mostrado a convidados para explicar que o histórico local é temporário. */
export const GUEST_HISTORY_NOTICE =
  "Estás em modo convidado: esta rota fica guardada só neste aparelho, por pouco tempo. Cria uma conta gratuita para manteres o teu histórico sempre disponível.";

export type GuestRecentItinerary = {
  id: string;
  title: string;
  city: string;
  createdAt: string;
  stops: SavedStop[];
};

/** Guarda uma rota concluída localmente para convidados (sem conta). */
export function saveGuestRecentItinerary(
  entry: Omit<GuestRecentItinerary, "id" | "createdAt"> & { id?: string },
): GuestRecentItinerary {
  const item: GuestRecentItinerary = {
    id: entry.id ?? `guest-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title: entry.title,
    city: entry.city,
    stops: entry.stops,
    createdAt: new Date().toISOString(),
  };
  try {
    const list = listGuestRecentItineraries();
    const next = [item, ...list.filter((r) => r.id !== item.id)].slice(0, GUEST_RECENT_LIMIT);
    safeLocalStorageSet(GUEST_RECENT_KEY, JSON.stringify(next));
  } catch {
    // armazenamento indisponível — recuperação local é apenas melhor-esforço
  }
  return item;
}

export function listGuestRecentItineraries(): GuestRecentItinerary[] {
  try {
    const raw = safeLocalStorageGet(GUEST_RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function clearGuestRecentItineraries(): void {
  safeLocalStorageRemove(GUEST_RECENT_KEY);
}

export async function listItineraries(userId?: string) {
  let query = supabase
    .from("saved_itineraries")
    .select("id,user_id,title,city,country,lang,mode,start_time,duration_hours,notes,share_slug,created_at,updated_at")
    .order("created_at", { ascending: false })
    .limit(100);
  if (userId) query = query.eq("user_id", userId);
  const { data, error } = await query;
  if (error) throw error;
  return ((data ?? []) as Array<Omit<SavedItinerary, "stops">>).map((row) => ({
    ...row,
    stops: [],
    stops_count: null,
  })) as SavedItinerary[];
}

export async function getItinerary(id: string) {
  const { data, error } = await supabase
    .from("saved_itineraries")
    .select("*")
    .eq("id", id)
    .single();
  if (error) throw error;
  return data as unknown as SavedItinerary;
}

export const deleteItineraryServer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => {
    const input = data as { id?: unknown };
    if (typeof input?.id !== "string" || !input.id) {
      throw new Error("Rota inválida");
    }
    return { id: input.id };
  })
  .handler(async ({ data, context }) => {
    const { data: deleted, error } = await context.supabase
      .from("saved_itineraries")
      .delete()
      .eq("id", data.id)
      .eq("user_id", context.userId)
      .select("id");

    if (error) throw new Error(error.message || "Erro a apagar rota");
    if (!deleted || deleted.length === 0) {
      throw new Error("Não foi possível apagar a rota. Tenta iniciar sessão novamente.");
    }

    return deleted[0];
  });

export async function deleteItinerary(id: string) {
  try {
    return await deleteItineraryServer({ data: { id } });
  } catch (serverError) {
    const message = (serverError as Error).message || "";
    if (message.toLowerCase().includes("unauthorized")) {
      throw new Error("Sessão expirada. Entra novamente para apagar a rota.");
    }
    if (message) throw serverError;
  }

  const { data, error } = await supabase
    .from("saved_itineraries")
    .delete()
    .eq("id", id)
    .select("id");
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("Não foi possível apagar a rota. Tenta iniciar sessão novamente.");
  }
  return data[0];
}

// Generates a short, URL-safe slug
function generateSlug(len = 10) {
  const alphabet = "abcdefghijkmnpqrstuvwxyz23456789"; // no 0/o/l/1 to avoid confusion
  let out = "";
  const bytes = new Uint8Array(len);
  (globalThis.crypto ?? (globalThis as unknown as { msCrypto: Crypto }).msCrypto).getRandomValues(bytes);
  for (let i = 0; i < len; i++) out += alphabet[bytes[i] % alphabet.length];
  return out;
}

/**
 * Ensures a share_slug exists on the itinerary and returns it.
 * Private itineraries can never be shared, even if they already have a slug
 * from before being marked private.
 */
export async function ensureShareSlug(id: string, existing: string | null): Promise<string> {
  const { data: row, error: rowError } = await supabase
    .from("saved_itineraries")
    .select("share_slug, private")
    .eq("id", id)
    .single();
  if (rowError) throw rowError;
  if (row?.private) {
    throw new Error("Esta rota é privada. Torna-a pública para poderes partilhá-la.");
  }
  if (row?.share_slug) return row.share_slug;
  if (existing) return existing;
  // Retry once on rare unique collision
  for (let attempt = 0; attempt < 3; attempt++) {
    const slug = generateSlug(10);
    const { data, error } = await supabase
      .from("saved_itineraries")
      .update({ share_slug: slug })
      .eq("id", id)
      .is("share_slug", null)
      .select("share_slug")
      .maybeSingle();
    if (!error && data?.share_slug) return data.share_slug;
    if (!error && !data) {
      // Row already has a slug — fetch it
      const { data: row } = await supabase
        .from("saved_itineraries")
        .select("share_slug")
        .eq("id", id)
        .single();
      if (row?.share_slug) return row.share_slug;
    }
    if (error && !`${error.message}`.toLowerCase().includes("unique")) throw error;
  }
  throw new Error("Não foi possível gerar link de partilha");
}

/**
 * Reads a shared itinerary by slug. The RPC itself already excludes private
 * routes and personal fields (notes, owner id) at the database level, so no
 * account is required and no personal data ever leaves the server.
 */
export async function getItineraryBySlug(slug: string): Promise<SavedItinerary | null> {
  const { data, error } = await (supabase as any)
    .rpc("get_shared_itinerary_by_slug", { p_slug: slug })
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return { ...data, user_id: "", notes: null, private: false } as unknown as SavedItinerary;
}

/** Revokes a share link — the URL stops working immediately. */
export async function revokeShareSlug(id: string): Promise<void> {
  const { error } = await supabase
    .from("saved_itineraries")
    .update({ share_slug: null })
    .eq("id", id);
  if (error) throw error;
}

/** Rotates the share token: revokes the old link and issues a brand new one. */
export async function regenerateShareSlug(id: string): Promise<string> {
  await revokeShareSlug(id);
  return ensureShareSlug(id, null);
}

export const PENDING_ITINERARY_EVENT = "routify:pending-itinerary";

export function queueLoadItinerary(it: SavedItinerary) {
  try {
    safeSessionStorageSet(PENDING_KEY, JSON.stringify(it));
    if (typeof window !== "undefined") {
      safeDispatchEvent(new CustomEvent(PENDING_ITINERARY_EVENT));
    }
  } catch { /* ignore */ }
}

export function consumePendingItinerary(): SavedItinerary | null {
  try {
    const raw = safeSessionStorageGet(PENDING_KEY);
    if (!raw) return null;
    safeSessionStorageRemove(PENDING_KEY);
    return JSON.parse(raw) as SavedItinerary;
  } catch {
    return null;
  }
}
